import { cn } from '@plato/ui/util/classes';
import { useRef } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../accordion/accordion.tsx';
import { Text } from '../ui/text.tsx';
import { CheckboxFilter } from './checkbox-filter.tsx';
import { DateRangeFilter } from './date-range-filter.tsx';
import { NumberRangeFilter } from './number-range-filter.tsx';
import { PriceRangeFilter } from './price-range-filter.tsx';
import { RadioFilter } from './radio-filter.tsx';
import type { AnyFilter, FilterOptionWithValue } from './types/filter-types.ts';

type AccordionFiltersProps<T extends AnyFilter> = {
    filters: FilterOptionWithValue<T>[];
    onFilterChange: (filterId: string, value: unknown) => void;
    defaultCurrency: string;
    className?: string;
};

function FilterContent({
    filter,
    onFilterChange,
    defaultCurrency,
}: {
    filter: FilterOptionWithValue;
    onFilterChange: (id: string, value: unknown) => void;
    defaultCurrency: string;
}) {
    const handleChange = (value: unknown) => {
        onFilterChange(filter.id, value);
    };

    switch (filter.type) {
        case 'checkbox':
            return <CheckboxFilter filter={filter} onChange={handleChange} />;
        case 'radio':
            return <RadioFilter filter={filter} onChange={handleChange} />;
        case 'dateRange':
            return <DateRangeFilter filter={filter} onChange={handleChange} />;
        case 'priceRange':
            return (
                <PriceRangeFilter filter={filter} onChange={handleChange} defaultCurrency={defaultCurrency} />
            );
        case 'numberRange':
            return <NumberRangeFilter filter={filter} onChange={handleChange} />;
        default:
            return null;
    }
}

export function AccordionFilters<T extends AnyFilter>({
    filters,
    onFilterChange,
    className,
    defaultCurrency,
}: AccordionFiltersProps<T>) {
    const accordionContainerRef = useRef<HTMLDivElement>(null);

    const getSelectedCount = (filter: FilterOptionWithValue<T>) => {
        switch (filter.type) {
            case 'checkbox':
                return filter.selectedValues ? filter.selectedValues.length : 0;
            case 'radio':
                return filter.selectedValue !== null && filter.selectedValue !== undefined ? 1 : 0;
            case 'dateRange':
                return filter.selectedRange &&
                    (('from' in filter.selectedRange &&
                        (filter.selectedRange.from || filter.selectedRange.to)) ||
                        ('unit' in filter.selectedRange && filter.selectedRange.count))
                    ? 1
                    : 0;
            case 'priceRange':
            case 'numberRange':
                return filter.selectedRange &&
                    (filter.selectedRange.min !== undefined || filter.selectedRange.max !== undefined)
                    ? 1
                    : 0;
            default:
                return 0;
        }
    };

    // Categories with applied filters open expanded so active state is visible; everything
    // else stays collapsed to keep the category overview scannable. A panel with a single
    // category auto-opens — collapsing buys no scannability there.
    const applied = filters.filter((f) => getSelectedCount(f) > 0).map((f) => f.id);
    const onlyFilter = filters.length === 1 ? filters[0] : undefined;
    const initiallyOpenIds = applied.length > 0 ? applied : onlyFilter ? [onlyFilter.id] : [];

    const scrollToNextFilter = (id: string) => {
        if (!id) {
            return;
        }

        // wait a couple frames so the accordion expansion animation/layout settles
        window.requestAnimationFrame(() => {
            try {
                const container = accordionContainerRef.current;
                const item = container?.querySelector(`[data-filter-id="${id}"]`) as HTMLElement | null;
                if (!container || !item) {
                    return;
                }

                const isOpen = item.dataset.state === 'open';

                if (isOpen) {
                    setTimeout(() => {
                        item.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }, 0);
                }
            } catch (e) {
                console.debug('Failed to scroll to next filter', e);
            }
        });
    };

    return (
        <div
            className={cn('bg-white border rounded-lg flex flex-col h-full', className)}
            ref={accordionContainerRef}
        >
            <Accordion type="multiple" className="w-full" defaultValue={initiallyOpenIds}>
                {filters.map((filter) => {
                    const selectedFilterCount = getSelectedCount(filter);
                    return (
                        <AccordionItem
                            key={filter.id}
                            value={filter.id}
                            className="border-b last:border-b-0"
                            data-filter-id={filter.id}
                        >
                            <AccordionTrigger
                                className="px-4 py-2 hover:no-underline"
                                onClick={() => {
                                    scrollToNextFilter(filter.id);
                                }}
                            >
                                <div className="flex items-center justify-between w-full">
                                    <Text weight="medium" color="neutral-950">
                                        {filter.label}
                                        {selectedFilterCount > 0 && ` (${selectedFilterCount})`}
                                    </Text>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 py-2 overflow-y-auto min-h-0 max-h-[50vh]">
                                <div data-filter-content-id={filter.id}>
                                    <FilterContent
                                        filter={filter}
                                        onFilterChange={onFilterChange}
                                        defaultCurrency={defaultCurrency}
                                    />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
}
